import { useTranslation } from 'next-i18next'
import { MessageOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Radio, Tooltip } from 'antd'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { Gender, GENDER } from '@/constants/gender'
import { useGetRandomName } from '@/hooks/useGetRandomName'
import {
  useCheckVoiceNameMutation,
  useEditVoiceMutation,
} from '@/redux/api/myavatarApi'

type VoiceEditModalProps = {
  isOpened: boolean
  onClose: () => void

  voiceId: string
  voiceName: string
  gender: Gender
}

export const VoiceEditModal: FC<VoiceEditModalProps> = ({
  isOpened,
  onClose,

  voiceId,
  voiceName: initialVoiceName,
  gender: initialGender,
}) => {
  const { t } = useTranslation('common')

  const { getRandomName } = useGetRandomName()

  const [mutateCheckVoiceName] = useCheckVoiceNameMutation()

  const [mutateEditVoice] = useEditVoiceMutation()

  const [isMutating, setIsMutating] = useState(false)

  const [voiceName, setVoiceName] = useState(initialVoiceName)
  const [gender, setGender] = useState(initialGender)
  const [isError, setIsError] = useState(false)

  const trimmedVoiceName = useMemo(() => voiceName.trim(), [voiceName])

  useEffect(() => {
    setVoiceName(initialVoiceName)
    setGender(initialGender)
  }, [isOpened, initialVoiceName, initialGender])

  useEffect(() => {
    setIsError(false)
  }, [voiceName])

  const handleSubmit = useCallback(async () => {
    if (isMutating) {
      return
    }

    if (!trimmedVoiceName) {
      return
    }

    setIsMutating(true)

    try {
      const resultCheckVoiceName = await mutateCheckVoiceName({
        name: trimmedVoiceName,
        id: voiceId,
      }).unwrap()

      /**
       * 조회 결과가 실패이거나, 이미 존재하는 이름이라면 종료
       */
      if (!resultCheckVoiceName.success || !resultCheckVoiceName.data) {
        setIsError(true)

        return
      }

      await mutateEditVoice({
        id: voiceId,
        text_intl: {
          en: trimmedVoiceName,
        },
        gender,
      }).unwrap()

      onClose()
    } finally {
      setIsMutating(false)
    }
  }, [
    isMutating,
    mutateCheckVoiceName,
    mutateEditVoice,
    voiceId,
    trimmedVoiceName,
    gender,
    onClose,
  ])

  return (
    <Modal
      open={isOpened}
      okButtonProps={{ loading: isMutating }}
      okText={t('OK')}
      onOk={handleSubmit}
      cancelText={t('Cancel')}
      onCancel={onClose}
      title={t('Edit voice')}
      destroyOnClose
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '10px',
          padding: '20px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
            paddingLeft: 'calc(35px + 10px)',
          }}
        >
          <Tooltip
            title={isError ? t('The name you entered already exists.') : ''}
            open={isError}
          >
            <Input
              type="text"
              value={voiceName}
              placeholder={t('Register your Voice Cloning name.')}
              onChange={(e) => {
                setVoiceName(e.target.value)
              }}
              status={isError ? 'error' : ''}
              style={{
                width: '320px',
                fontSize: '14px',
                fontWeight: '700',
                lineHeight: '20px',
                textAlign: 'center',
                padding: '10px 7px',
              }}
            />
          </Tooltip>
          <Button
            type="primary"
            size="large"
            shape="circle"
            icon={<MessageOutlined />}
            onClick={() => {
              setVoiceName(getRandomName())
            }}
          />
        </div>
        <Radio.Group
          onChange={(e) => setGender(e.target.value)}
          name="avatar-gender"
          value={gender}
        >
          <Radio value={GENDER.MALE} checked={gender === GENDER.MALE}>
            {t('Male')}
          </Radio>
          <Radio value={GENDER.FEMALE} checked={gender === GENDER.FEMALE}>
            {t('Female')}
          </Radio>
        </Radio.Group>
      </div>
    </Modal>
  )
}
